import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, IntlProvider } from "react-intl";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faBars, faBell, faUser } from "@fortawesome/free-solid-svg-icons";
import Loader from "react-loader-spinner";
import { acquireGraphToken, acquireToken } from "./adalConfig";
import { IApplicationState } from "./models/interfaces/IApplicationState";
import { fetchCurrentUser } from "./store/currentUser/actions";
import { fetchSettings } from "./store/settings/actions";
import { translationSets } from "./assets/translations";
import { decideLanguageToUse } from "./common/helperFunctions/LanguageHelpers";
import { NavBar } from "./components/shellComponents/navBar/NavBar";
import { PageContainer } from "./components/shellComponents/pageContainer/PageContainer";
import { CheckTenantSubscriptionStatus } from "./common/helperFunctions/SubscriptionHelpers";
import moment from "moment";
import "moment/locale/nl";
import { SubscriptionStatus } from "@one/core";
import { initializeIcons } from "@fluentui/react";
import { OneThemeProvider, OneThemes } from "./styles/themes/OneThemeProvider";
import { TenantStatus } from "./enums/TenantStatus";
import SetupTenantComponent from "./components/shellComponents/registerNewTenantComponent/SetupTenantComponent";
import { tenantService } from "./services/TenantService";

//@ts-ignore
library.add(faBars, faUser, faBell);

const App = () => {
  initializeIcons(
    "https://res.cdn.office.net/files/fabric-cdn-prod_20241209.001/assets/icons/",
  );
  const dispatch = useDispatch();
  const settings = useSelector((state: IApplicationState) => state.settingsSlice);

  const [tenantStatus, setTenantStatus] = useState<TenantStatus>(TenantStatus.Fetching);
  const [isCheckingTenantStatus, setIsCheckingTenantStatus] = useState(true);
  const [tenantSubscriptionStatus, setTenantSubscriptionStatus] = useState(SubscriptionStatus.None);
  const [isCheckingSubscriptionStatus, setIsCheckingSubscriptionStatus] = useState(false);
  const localeSetting = decideLanguageToUse(settings);
  moment.locale(localeSetting);

  useEffect(() => {
    tenantService.doesTenantExist()
      .then((status) => {
        setTenantStatus(status);
      })
      .finally(() => setIsCheckingTenantStatus(false));
  }, []);

  useEffect(() => {
    if (tenantStatus === TenantStatus.Fetching || tenantStatus === TenantStatus.Error || tenantStatus === TenantStatus.DoesNotExist) {
      return;
    }
    setIsCheckingSubscriptionStatus(true);

    const checkTenantHasAcces = async () => {
      setTenantSubscriptionStatus(await CheckTenantSubscriptionStatus());
      setIsCheckingSubscriptionStatus(false);
    };

    dispatch(fetchCurrentUser());
    dispatch(fetchSettings());
    checkTenantHasAcces();
  }, [dispatch, tenantStatus]);

  // init token refresher
  const appTokenRefresher = () => {
    acquireToken();
    acquireGraphToken();
  };

  // Sets an interval that refreshes to get the token every 15 minutes
  // We use this because our custom API's do not automatically issue a
  // refresh token.
  setInterval(appTokenRefresher, 90000);

  const appContent = useMemo(() => {
    if (isCheckingSubscriptionStatus || isCheckingTenantStatus) {
      return (
        <div className="app-centered">
          <div className="loaderWrapper">
            <Loader type="ThreeDots" color="#00BFFF" height={50} width={50} />
          </div>
        </div>
      );
    }

    if (tenantStatus === TenantStatus.DoesNotExist) {
      return (
        <SetupTenantComponent onTenantCreated={() => setTenantStatus(TenantStatus.Exists)} />
      )
    }

    if (tenantSubscriptionStatus === SubscriptionStatus.Active) {
      return (
        <div className="app">
          <React.Fragment>
            <NavBar />
            <PageContainer />
          </React.Fragment>
        </div>
      );
    }

    if (tenantSubscriptionStatus === SubscriptionStatus.Inactive) {
      return (
        <div className="app-centered">
          <h1>
            <FormattedMessage id="subscription.inactive" defaultMessage="Uw abonnement is verlopen." />
          </h1>
        </div>
      );
    }

    if (tenantSubscriptionStatus === SubscriptionStatus.None) {
      return (
        <div className="app-centered">
          <h1>
            <FormattedMessage id="subscription.none" defaultMessage="U heeft geen abonnement voor One." />
          </h1>
        </div>
      );
    }

    if (tenantSubscriptionStatus === SubscriptionStatus.Error) {
      return (
        <div className="app-centered">
          <h1>
            <FormattedMessage id="subscription.error" defaultMessage="Er is iets fout gegaan bij het ophalen van uw abonnement." />
          </h1>
        </div>
      );
    }

    return null;
  }, [isCheckingSubscriptionStatus, isCheckingTenantStatus, tenantSubscriptionStatus, tenantStatus]);

  return (
    <IntlProvider locale={localeSetting} messages={translationSets[localeSetting]}>
      <OneThemeProvider theme={OneThemes.Default}>{appContent}</OneThemeProvider>
    </IntlProvider>
  );
};

export default App;
